import { useQuery } from '@tanstack/react-query';
import { opsApi } from '@/api/ops';
import type { ChildWallet, LivePanel, WalletBalance } from '@/api/opsTypes';
import { StatusChip } from '@/components/ops/Badges';
import { KeyValues, Section } from '@/components/ops/Layout';
import { QueryState } from '@/components/ops/Problem';
import { formatCredits, isValidScale, scaleText } from '@/ops/credits';
import { LIVE } from '@/ops/live';
import { useCreditScale } from '@/ops/useCreditScale';
import { PanelProblem } from './UsageFootprintTab';

/**
 * The tenant's current AI and SMS/WhatsApp credit balances, read LIVE from each product-local wallet (CP2 — the console
 * stores no balance). A wallet whose service is down shows its own labelled "unavailable" panel, never a zero — the other
 * wallet still shows. Figures are micro-credits displayed with billing's live scale; when that scale is unknown they are
 * raw micro-credits, labelled — never divided by an assumed 100 (money review 2026-10-04). A parent whose children each
 * have their own wallet (`per_child`) lists them.
 */
export function CreditsCard({ env, tenantId }: { env: string; tenantId: string }) {
  const credits = useQuery({
    queryKey: ['ops', 'credits', env, tenantId],
    queryFn: () => opsApi.tenants.credits(env, tenantId),
    ...LIVE,
  });
  const { scale } = useCreditScale(env);

  return (
    <Section title="Credits" meta="LIVE from each wallet — nothing is stored in the console">
      <QueryState query={credits} service="ai">
        {(data) => (
          <div className="grid-2">
            <WalletFigures title="AI credits" panel={data.ai} env={env} scale={scale} />
            <WalletFigures title="SMS & WhatsApp credits" panel={data.messaging} env={env} scale={scale} />
          </div>
        )}
      </QueryState>
    </Section>
  );
}

function statusLine(w: { available: number; overdrawn?: boolean | null; credit_mode?: string | null }): string | null {
  if (w.credit_mode === 'postpaid') {
    return `Postpaid — sends are never refused; usage is invoiced${w.available < 0 || w.overdrawn ? ' (overdrawn now)' : ''}`;
  }
  if (w.overdrawn || w.available < 0) return 'Overdrawn — new sends and leases are refused until it is topped up';
  if (w.available <= 0) return 'Empty — sends that use this wallet are refused';
  return null;
}

export function WalletFigures({ title, panel, env, scale }: {
  title: string;
  panel: LivePanel<WalletBalance>;
  env: string;
  /** Billing's live scale; null = unknown here (the wallet's own reported scale is tried next). */
  scale: number | null;
}) {
  const heading = <h3 className="t-heading" style={{ margin: '0 0 8px' }}>{title}</h3>;
  if (panel.status !== 'ok' || !panel.data) {
    // Per wallet: the OTHER wallet still shows its figures — this one says which read failed, and shows none.
    return (
      <div data-wallet-failed={title}>
        {heading}
        <p className="t-caption note-error" style={{ margin: '0 0 6px' }}>Could not read the {title} wallet — no balance is shown.</p>
        <PanelProblem panel={panel} env={env} what={`${title} balance read`} />
      </div>
    );
  }

  const w = panel.data;
  // Billing's scale first; else the one the wallet's service read from billing; null stays UNKNOWN (never 100).
  const shown = isValidScale(scale) ? scale : isValidScale(w.credit_display_scale) ? w.credit_display_scale : null;
  const held = w.held_credits ?? 0;
  const leased = w.leased_credits ?? 0;
  const available = w.available_credits ?? w.balance_credits - held - leased;
  const status = statusLine({ available, overdrawn: w.overdrawn, credit_mode: w.credit_mode });
  const children = w.children ?? [];
  return (
    <div>
      {heading}
      <KeyValues
        rows={[
          { label: 'Available now', value: <strong>{formatCredits(available, shown)}</strong> },
          { label: 'Balance', value: formatCredits(w.balance_credits, shown) },
          ...(held > 0 ? [{ label: 'Held for work in progress', value: formatCredits(held, shown) }] : []),
          ...(leased > 0 ? [{ label: 'Lent to WhatsApp sending', value: formatCredits(leased, shown) }] : []),
          ...(w.wallet_mode === 'per_child' ? [{ label: 'Wallets', value: <span className="t-caption">Each child account has its own wallet (below)</span> }] : []),
          ...(status ? [{ label: 'Status', value: <span className="t-caption">{status}</span> }] : []),
          ...(shown === null ? [{ label: 'Display scale', value: <span className="t-caption">1 credit = {scaleText(null)} micro-credits — figures are raw micro-credits</span> }] : []),
        ]}
      />
      {children.length > 0 && <ChildWallets wallets={children} scale={shown} />}
    </div>
  );
}

function ChildWallets({ wallets, scale }: { wallets: ChildWallet[]; scale: number | null }) {
  return (
    <div className="table-wrap" style={{ marginTop: 12 }}>
      <table className="table">
        <caption className="t-label" style={{ textAlign: 'start' }}>Child accounts ({wallets.length})</caption>
        <thead><tr><th>Account</th><th className="num">Available</th><th className="num">Balance</th><th className="num">Held · lent</th></tr></thead>
        <tbody>
          {wallets.map((c) => (
            <tr key={c.account_id}>
              <td>
                <div>{c.name}</div>
                <div className="t-caption muted">{c.external_ref ?? c.account_id}</div>
                <span className="row">
                  {c.status !== 'active' && <StatusChip status={c.status} />}
                  {(c.overdrawn || c.available_credits < 0) && <StatusChip status="overdrawn" tone="danger" />}
                </span>
              </td>
              <td className="num"><strong>{formatCredits(c.available_credits, scale)}</strong></td>
              <td className="num">{formatCredits(c.balance_credits, scale)}</td>
              <td className="num t-caption">{formatCredits(c.held_credits, scale)} · {formatCredits(c.leased_credits, scale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
